import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../../../api/client'
import { useCreateOnboardingIntake } from '../../../api/hooks'
import type { OnboardingIntakeResult, OnboardingRunScope, OnboardingRunTargetInput } from '../../../api/onboardingTypes'
import { track } from '../../../analytics/events'
import { createDebug } from '../../../utils/debugLog'
import RecipientHistory from './RecipientHistory'
import { PRIMARY_BUTTON_CLASS } from './ui'

const log = createDebug('dw:onboarding:direct-intake')
const ignoreHistory = () => {}

/** Mounted with an input key: edits invalidate preview and all acknowledgements. */
export default function DirectIntakePreview({ targets, reason, sharedTeam, onCreated, feedbackContainer }: {
  targets: OnboardingRunTargetInput[]
  feedbackContainer: HTMLElement | null
  reason: string
  sharedTeam: boolean
  onCreated: (scopes: OnboardingRunScope[]) => void
}) {
  const { t } = useTranslation('pages')
  const intake = useCreateOnboardingIntake()
  const [preview, setPreview] = useState<OnboardingIntakeResult | null>(null)
  const [acknowledged, setAcknowledged] = useState<Record<string, string>>({})
  const [checking, setChecking] = useState(true)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [checkAttempt, setCheckAttempt] = useState(0)
  const submitting = useRef(false)
  const retryCheck = () => {
    if (checking || submitting.current) return
    intake.reset()
    setCheckAttempt((attempt) => attempt + 1)
  }
  useEffect(() => {
    let current = true
    setChecking(true)
    setPreview(null)
    setAcknowledged({})
    setValidationError(null)
    const timer = setTimeout(() => {
      void api.createOnboardingIntake({ reason, targets, sharedTeam, validateOnly: true, acknowledgedPriorTargets: {} }).then((result) => {
        if (!current) return
        if (result.recipients.length !== targets.length || result.recipients.some((row, index) => row.history.email !== targets[index].email)) {
          setValidationError(t('adminOnboarding.history.historyUnavailable'))
        } else setPreview(result)
        setChecking(false)
      }).catch((error) => {
        if (!current) return
        setValidationError(error instanceof Error ? error.message : t('adminOnboarding.history.historyUnavailable'))
        setChecking(false)
      })
    }, 500)
    return () => { current = false; clearTimeout(timer) }
  }, [reason, targets, sharedTeam, t, checkAttempt])
  const ready = !checking && preview !== null && preview.recipients.length === targets.length && preview.recipients.every(({ history }) =>
    history.classification === 'new' || (history.classification === 'requires_acknowledgement'
      && history.selected != null && acknowledged[history.email] === history.selected.target_id))
  const submit = async () => {
    if (!ready || intake.isPending || submitting.current) return
    submitting.current = true
    track('admin_onboarding_intake_submitted', { onboarding_mode: 'single', count: targets.length })
    log('submitted recipient_count=%d shared_team=%s', targets.length, sharedTeam)
    try {
      const result = await intake.mutateAsync({ reason, targets, sharedTeam, validateOnly: false, acknowledgedPriorTargets: acknowledged })
      onCreated(result.scopes)
    } catch {
      setPreview(null)
      setAcknowledged({})
    } finally {
      submitting.current = false
    }
  }
  return <section className="flex flex-col gap-3" data-testid="onboarding-direct-preview">
    {checking && <p className="text-xs text-muted-foreground" data-testid="onboarding-checking-recipients">{t('adminOnboarding.direct.checking')}</p>}
    {preview && <p className="text-sm text-muted-foreground" data-testid="onboarding-direct-total">{t('adminOnboarding.direct.plannedTotal', { amount: preview.total_credits })}</p>}
    {targets[0]?.team_mode && <p className="text-sm text-muted-foreground" data-testid="onboarding-direct-billing-summary">
      {t(targets[0].credit_billing_mode === 'postpaid' ? 'adminOnboarding.team.billingSummaryPostpaid' : 'adminOnboarding.team.billingSummaryPrepaid', {
        mode: t(`adminOnboarding.team.billingMode.${targets[0].credit_billing_mode ?? 'prepaid'}`),
        anchor: targets[0].billing_period_anchor ?? '', months: targets[0].billing_period_months ?? '',
        timezone: targets[0].billing_timezone ?? '',
      })}
    </p>}
    {feedbackContainer && createPortal(<>
      {validationError && <p role="alert" className="text-sm text-warning" data-testid="onboarding-intake-error">{validationError}</p>}
      {intake.error instanceof Error && <p role="alert" className="text-sm text-destructive" data-testid="onboarding-intake-error">{intake.error.message}</p>}
      {(validationError || intake.error) && !checking && <button type="button" className="self-start text-sm text-primary underline" onClick={retryCheck} data-testid="onboarding-retry-check">{t('adminOnboarding.direct.retryCheck')}</button>}
      {preview && preview.recipients.map(({ target, history }) => <div key={target.email} data-testid={`onboarding-direct-recipient-${target.email}`}>
        <p className="mb-1 text-sm text-foreground">{target.email}</p>
        <RecipientHistory email={target.email} history={history} onHistory={ignoreHistory} lookupEnabled={false}
          acknowledged={acknowledged[target.email] === history.selected?.target_id}
          onAcknowledged={(checked) => setAcknowledged((current) => {
            const next = { ...current }
            if (checked && history.selected) next[target.email] = history.selected.target_id
            else delete next[target.email]
            return next
          })} />
      </div>)}
    </>, feedbackContainer)}
    {sharedTeam && <p className="text-sm font-medium text-foreground" data-testid="onboarding-submit-owner">{t('adminOnboarding.direct.ownerLabel')}: {targets[0]?.email}</p>}
    <button type="button" disabled={!ready || intake.isPending} className={PRIMARY_BUTTON_CLASS}
      data-testid="onboarding-start-button" onClick={() => void submit()}>{t('adminOnboarding.startButton')}</button>
  </section>
}
